import { css } from '@emotion/react';
import type { VotingAction } from '@hotjar/feedback/domain/common/Responses/VotingAction';
import {
  ThumbsUpDownTooltip,
  ThumbsUpDownFooter,
} from '@hotjar/webapp/components/ThumbsUpDownTooltip';
import { ThumbsUpDownTableButton } from '@hotjar/webapp/components/ThumbsUpDownTooltip/ThumbsUpDownTableButton';
import { useHelpHotjarUrl, useTranslation } from '@hotjar/translations';
import { sendResponseFeedback } from './infrastructure/ResponsesRepository';
import type { Answer } from './domain/Answer';
import { getAnswerForVote, getVotingActionForAnswer } from './domain/VotesAnswerMapper';

export type RenderReferenceFn = (props: {
  renderDefaultReference: () => JSX.Element;
}) => JSX.Element;

interface ResponseFeedbackTooltipProps {
  votingAction: VotingAction | null;
  siteId: number;
  feedbackId: number;
  responseId: number;
  onChange: (newVotingAction: VotingAction, responseId: number) => void;
  distance?: number;
  visible?: boolean;
  renderReference?: RenderReferenceFn;
  side?: 'left' | 'right';
  appendTo?: 'parent' | Element | ((ref: Element) => Element);
  color?: string;
}

export function ResponseFeedbackTooltip(props: ResponseFeedbackTooltipProps): JSX.Element {
  const helpHotjarUrl = useHelpHotjarUrl();
  const {
    votingAction,
    siteId,
    feedbackId,
    responseId,
    onChange,
    distance = 16,
    visible,
    renderReference,
    side,
    appendTo = 'parent',
    color,
  } = props;

  const { t } = useTranslation('feedback', { keyPrefix: 'survey-response.feedback-tooltip' });

  function handleAnswerChange(answer: Answer) {
    const newVotingAction = getVotingActionForAnswer(votingAction, answer);
    if (newVotingAction) {
      sendResponseFeedback(newVotingAction, siteId, feedbackId, responseId);
      onChange(newVotingAction, responseId);
    }
  }

  function renderDefaultReference(): JSX.Element {
    return (
      <ThumbsUpDownTableButton
        answer={getAnswerForVote(votingAction)}
        labelNoAnswer={t('no-answer-label')}
        color={color}
      />
    );
  }

  return (
    <ThumbsUpDownTooltip
      questionLabel={t('question-label')}
      thumbsUpLabel={t('thumbs-up-label')}
      thumbsDownLabel={t('thumbs-down-label')}
      onAnswerChange={handleAnswerChange}
      footer={
        <ThumbsUpDownFooter
          text={t('footer-text')}
          href={`${helpHotjarUrl}/categories/115001323487?section=115002315328&selectedArticle=115011609267`}
          css={css`
            min-width: 320px;
          `}
        />
      }
      isLoading={false}
      answer={getAnswerForVote(votingAction)}
      visible={visible}
      offset={side === 'right' ? 130 : -130}
      placement="bottom"
      distance={distance}
      interactiveBorder={20}
      appendTo={appendTo}
    >
      {renderReference?.({ renderDefaultReference }) ?? renderDefaultReference()}
    </ThumbsUpDownTooltip>
  );
}
